import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import "./scss/style.scss";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Dashboard from "./views/dashboard/Dashboard";
import ManageTransferOrders from "./views/manage-transfer-orders/ManageTransferOrders";
import NewBookingOrders from "./views/new-booking-orders/NewBookingOrders";
import ManageBookingOrders from "./views/manage-booking-orders/ManageBookingOrders";
import NewBookingOrderForm from "./views/new-booking-orders/NewBookingOrdersForm";
import ManageRoute from "./views/manage-route/ManageRoute";
import CreateTrip from "./views/manage-trip/CreateTrip";
import Reports from "./views/reports-management/Reports";
import UserTypeManagement from "./views/manage-user-type/UserTypeManagement";
import CreateUserTypeForm from "./views/manage-user-type/CreateUserTypeForm";
import UserManagement from "./views/manage-user/UserManagement";
import CreateUserForm from "./views/manage-user/CreateUserForm";
import StoreManagement from "./views/manage-store/StoreManagement";
import CreateStoreForm from "./views/manage-store/CreateStoreForm";
import PlasticSealManagement from "./views/manage-plastic-seal/PlasticSealManagement";
import CreatePlasticSealForm from "./views/manage-plastic-seal/CreatePlasticSealForm";
import ReportIssueManagement from "./views/manage-report-issue/ReportIssueManagement";
import ReportIssueForm from "./views/manage-report-issue/ReportIssueForm";

import UserActivity from "./views/user-activity/UserActivity";
import UserProfile from "./views/user-profile/UserProfile";
import CheckDeliveryStatus from "./views/check-delivery-status/CheckDeliveryStatus";
import TransferPackage from "./views/transfer-package/TransferPackage";
import { Toaster } from "react-hot-toast";
import CreateRoute from "./views/manage-route/ManageRouteForm";
import BulkStoreForm from "./views/manage-store/BulkStoreForm";
import CreateRoutePointForm from "./views/manage-route-point/CreateRoutePointForm";
import RoutePointManagement from "./views/manage-route-point/RoutePointManagement";
import { DataProvider } from "./context/DataContext";
import EditTrip from "./views/manage-trip/EditTrip";
import CountryManagement from "./views/manage-country/CountryManagement";
import CreateCountryForm from "./views/manage-country/CreateCountryForm";
import BulkCountryForm from "./views/manage-country/BulkCountryForm";
import useAuth from "./__custom_hook/useAuth";
import TripOrdersTable from "./views/manage-trip/TripOrdersTable";
import CreateBulkTrip from "./views/manage-trip/CreateBulkTrip";
import ManageTrip from "./views/manage-trip/ManageTrip";
import ManageGpsLocation from "./views/gps-location-management/ManageGpsLocation";
import ManageUnits from "./views/manage-units/ManageUnits";
import ManageTypes from "./views/manage-types/ManageTypes";
import ToReceiveBookingOrders from "./views/to-recive-booking-orders/ToReceiveBookingOrders";
import DriverManagementTable from "./views/manage-user/ManageUser/DrivererManagementTable";
import CreateDriverForm from "./views/manage-user/ManageUser/CreateDriver";
import CreateVehicleModal from "./components/CreateVehicleModal/CreateVehicleModal";
import CreateVehicle from "./components/CreateVehicleModal/CreateVehicleModal";
import VehicleManagementTable from "./components/CreateVehicleModal/ManageVehicle";
import ManageGpsLocationReport from "./views/gps-location-management/ManageGpsLocationReport";
const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

// Containers
const DefaultLayout = React.lazy(() => import("./layout/DefaultLayout"));

// Pages
const Login = React.lazy(() => import("./views/pages/login/Login"));
const Register = React.lazy(() => import("./views/pages/register/Register"));
const Page404 = React.lazy(() => import("./views/pages/page404/Page404"));
const Page500 = React.lazy(() => import("./views/pages/page500/Page500"));
// http://localhost:3000/manage-route
function App() {
  const { checkTokenExpiration } = useAuth();
  useEffect(() => {
    checkTokenExpiration(); // Check token expiration on component mount
  }, []);

  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <DataProvider>
        <Suspense fallback={loading}>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" />} />
            {/* <Route path="/" element={<Navigate to="/login" />} /> */}
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={<DefaultLayout />}>
              <Route path="dashboard" element={<Dashboard />} />

              <Route path="manage-user-type" element={<UserTypeManagement />} />
              <Route path="create-user-type" element={<CreateUserTypeForm />} />
              <Route
                path="edit-user-type/:id"
                element={<CreateUserTypeForm />}
              />

              <Route path="manage-user" element={<UserManagement />} />
              <Route path="manage-driver" element={<DriverManagementTable />} />
              <Route path="create-user" element={<CreateUserForm />} />
              <Route path="create-driver" element={<CreateDriverForm />} />
              <Route path="edit-user/:id" element={<CreateUserForm />} />
              <Route path="edit-driver/:id" element={<CreateDriverForm />} />

              <Route path="manage-store" element={<StoreManagement />} />
              <Route path="create-store" element={<CreateStoreForm />} />
              <Route path="create-bulk-store" element={<BulkStoreForm />} />
              <Route path="edit-store/:id" element={<CreateStoreForm />} />

              <Route
                path="manage-plastic-seal"
                element={<PlasticSealManagement />}
              />
              <Route
                path="create-plastic-seal"
                element={<CreatePlasticSealForm />}
              />

              <Route path="user-activity" element={<UserActivity />} />

              <Route
                path="new-transfer-orders"
                element={<ManageTransferOrders />}
              />

              <Route path="new-booking-orders" element={<NewBookingOrders />} />
              <Route
                path="update-booking-orders/:bookingOrderId"
                element={<NewBookingOrderForm />}
              />
              <Route
                path="manage-booking-orders"
                element={<ManageBookingOrders />}
              />
              <Route
                path="to-receive-booking-orders"
                element={<ToReceiveBookingOrders />}
              />
              <Route
                path="create-booking-order"
                element={<NewBookingOrderForm />}
              />

              <Route
                path="manage-route-point"
                element={<RoutePointManagement />}
              />
              <Route
                path="create-route-point"
                element={<CreateRoutePointForm />}
              />
              <Route
                path="edit-route-point/:id"
                element={<CreateRoutePointForm />}
              />

              <Route path="manage-route" element={<ManageRoute />} />
              <Route path="create-route" element={<CreateRoute />} />
              <Route path="edit-route/:id" element={<CreateRoute />} />

              <Route path="manage-trip" element={<ManageTrip />} />
              <Route
                path="manage-trip/orders/:id"
                element={<TripOrdersTable />}
              />

              <Route path="create-trip" element={<CreateTrip />} />
              <Route path="create-trip/:id" element={<CreateTrip />} />
              <Route path="create-bulk-trip/:id" element={<CreateBulkTrip />} />
              <Route path="edit-trip/:id" element={<EditTrip />} />

              <Route path="manage-country" element={<CountryManagement />} />
              <Route path="create-country" element={<CreateCountryForm />} />
              <Route path="create-vehicle" element={<CreateVehicle />} />
              <Route path="edit-vehicle/:id" element={<CreateVehicle />} />
              <Route path="manage-vehicle" element={<VehicleManagementTable />} />
              <Route path="create-bulk-country" element={<BulkCountryForm />} />
              <Route path="edit-country/:id" element={<CreateCountryForm />} />

              <Route path="report-issue" element={<ReportIssueManagement />} />
              <Route path="create-report-issue" element={<ReportIssueForm />} />

              <Route path="profile" element={<UserProfile />} />
              <Route path="gps-location" element={<ManageGpsLocation />} />
              <Route path="gps-location-report" element={<ManageGpsLocationReport />} />
              <Route path="units" element={<ManageUnits />} />
              <Route path="types" element={<ManageTypes />} />

              <Route
                path="check-delivery-status"
                element={<CheckDeliveryStatus />}
              />

              <Route path="transfer-package" element={<TransferPackage />} />

              <Route path="reports" element={<Reports />} />

              <Route path="*" element={<h1>Fallback route</h1>} />
            </Route>
            {/* Add other routes here */}
          </Routes>
        </Suspense>
        <Toaster />
      </DataProvider>
    </QueryClientProvider>
  );
}

export default App;
